// פונקציות תרגום מ־Enum לטקסט בעברית לפי סוג הביטוח עם שימוש ב־case ערכים טיפוסיים

import {
  MainInsuranceBranch,
  PlanClassification,
  PremiumType,
  InsuranceProductType,
  BusinessInsuranceSubCategory,
  CarInsuranceSubCategory,
  DisabilityInsuranceSubCategory,
  HealthSubCategory,
  HomeInsuranceSubCategory,
  LifeInsuranceSubCategory,
  LongTermCareSubCategory,
  LossOfWorkCapacitySubCategory,
  ServiceContractSubCategory,
  HealthCoverageNormalizedCategory
} from '../interfaces/DTO/insurance_data/InsuranceData';

// צבעים מנוגדים להבדלה בין פוליסות
export const POLICY_COLORS = [
  "#FF6B6B", // אדום בהיר
  "#4ECDC4", // טורקיז
  "#FFD93D", // צהוב
  "#6BCB77", // ירוק
  "#9D84B7", // סגול
  "#FF8C42", // כתום
  "#5DADE2", // כחול בהיר
  "#F39C12", // כתום כהה
  "#E74C3C", // אדום כהה
  "#1ABC9C", // ירוק ים
  "#9B59B6", // סגול כהה
  "#34495E", // אפור כחלחל
  "#16A085", // ירוק ים כהה
  "#D35400", // כתום חום
  "#C0392B", // אדום ענבר
];

/**
 * Format date to Hebrew locale
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format MainInsuranceBranch enum to Hebrew text
 */
export const formatMainInsuranceBranch = (value: MainInsuranceBranch): string => {
  switch (value) {
    case MainInsuranceBranch.CAR: return "רכב";
    case MainInsuranceBranch.HOME: return "דירה";
    case MainInsuranceBranch.BUSINESS: return "עסק";
    case MainInsuranceBranch.DISABILITY_LIFE: return "נכות וחיים";
    case MainInsuranceBranch.LIFE: return "חיים";
    case MainInsuranceBranch.SAVINGS: return "חיסכון";
    case MainInsuranceBranch.HEALTH_ACCIDENT: return "בריאות ותאונות";
    case MainInsuranceBranch.HEALTH: return "בריאות";
    case MainInsuranceBranch.LONG_TERM_CARE: return "סיעוד";
    case MainInsuranceBranch.SERVICE_CONTRACT: return "כתב שירות";
    case MainInsuranceBranch.LOSS_OF_WORK_CAPACITY: return "אובדן כושר עבודה";
    default: return value;
  }
};

/**
 * Format PlanClassification enum to Hebrew text
 */
export const formatPlanClassification = (value: PlanClassification): string => {
  switch (value) {
    case PlanClassification.INSURANCE: return "ביטוח";
    case PlanClassification.SERVICE: return "שירות";
    case PlanClassification.PERSONAL: return "פרטי";
    case PlanClassification.GROUP: return "קבוצתי";
    case PlanClassification.KUPAT_HOLIM_GROUP: return "קופת חולים";
    default: return value;
  }
};

/**
 * Format PremiumType enum to Hebrew text
 */
export const formatPremiumType = (value: PremiumType): string => {
  switch (value) {
    case PremiumType.ANNUAL: return "שנתי";
    case PremiumType.MONTHLY: return "חודשי";
    case PremiumType.ONE_TIME: return "חד פעמי";
    default: return value;
  }
};

/**
 * Format InsuranceProductType enum to Hebrew text
 */
export const formatInsuranceProductType = (value: InsuranceProductType): string => {
  switch (value) {
    case InsuranceProductType.INSURANCE_POLICY: return "פוליסת ביטוח";
    case InsuranceProductType.ROADSIDE_SERVICE: return "שירותי דרך";
    case InsuranceProductType.ROADSIDE_SERVICE_EXTENDED: return "שירותי דרך מורחב";
    case InsuranceProductType.REPLACEMENT_CAR: return "רכב חלופי";
    case InsuranceProductType.REPLACEMENT_CAR_HOME: return "רכב חלופי לבית";
    case InsuranceProductType.WINDOWS: return "חלונות";
    case InsuranceProductType.WINDOWS_EXTENDED: return "חלונות מורחב";
    case InsuranceProductType.LIGHTS_AND_MIRRORS: return "פנסים ומראות";
    case InsuranceProductType.RADIO_REPLACEMENT: return "החלפת רדיו";
    case InsuranceProductType.WATER_DAMAGE: return "נזקי מים";
    case InsuranceProductType.WATER_DAMAGE_EXTENDED: return "נזקי מים מורחב";
    case InsuranceProductType.LEGAL_ASSISTANCE: return "סיוע משפטי";
    case InsuranceProductType.CONSULTATION: return "ייעוץ";
    case InsuranceProductType.SURGERY_STANDARD: return "ניתוחים אחידה";
    case InsuranceProductType.TRANSPLANT_STANDARD: return "השתלות רגילות";
    case InsuranceProductType.MEDICINE_OUT_OF_BASKET: return "תרופות מחוץ לסל";
    case InsuranceProductType.SURGERY_ABROAD_STANDARD: return 'ניתוחים בחו"ל רגילים';
    case InsuranceProductType.ADVANCED_TREATMENT_EQUIPMENT: return "ציוד מתקדם";
    case InsuranceProductType.MEDICAL_SUPPORT: return "תמיכה רפואית";
    case InsuranceProductType.AMBULATORY_OTHER: return "טיפול אמבולטורי אחר";
    case InsuranceProductType.ALT_MEDICINE: return "רפואה משלימה";
    case InsuranceProductType.RAPID_DIAGNOSIS: return "אבחון מהיר";
    case InsuranceProductType.HOSPITAL_ESCORT: return "ליווי באשפוז";
    case InsuranceProductType.ILLNESS_ESCORT: return "ליווי במחלה";
    case InsuranceProductType.PERSONAL_MEDICAL_MANAGEMENT: return "ניהול רפואי אישי";
    case InsuranceProductType.ONLINE_DOCTOR: return "רופא אונליין";
    case InsuranceProductType.ONLINE_DOCTOR_ABROAD: return 'רופא אונליין בחו"ל';
    case InsuranceProductType.SPORTS_MEDICINE: return "רפואת ספורט";
    case InsuranceProductType.SUPPLEMENTARY_SURGERY_SHABAN: return 'ניתוחים משלים שב"ן';
    case InsuranceProductType.SURGERY_INDEMNITY: return 'ניתוחים עם פיצוי על פנייה לציבורי/שב"ן';
    case InsuranceProductType.RANSPLANTS: return "השתלות";
    case InsuranceProductType.SURGERY_ABROAD: return 'ניתוחים בחו"ל';
    case InsuranceProductType.BASIC_MEDICINES: return "תרופות - בסיס";
    default: return value;
  }
};

/**
 * Format LongTermCareSubCategory enum to Hebrew text
 */
export const formatLongTermCareSubCategory = (value: LongTermCareSubCategory): string => {
  switch (value) {
    case LongTermCareSubCategory.UP_TO_3_MONTHS: return "המתנה עד 3 חודשים";
    default: return value;
  }
};

/**
 * Format CarInsuranceSubCategory enum to Hebrew text
 */
export const formatCarInsuranceSubCategory = (value: CarInsuranceSubCategory): string => {
  switch (value) {
    case CarInsuranceSubCategory.COMPULSORY: return "חובה";
    case CarInsuranceSubCategory.COMPREHENSIVE: return "מקיף";
    case CarInsuranceSubCategory.THIRD_PARTY: return "צד ג׳";
    case CarInsuranceSubCategory.COMPREHENSIVE_NO_ACCIDENT: return "מקיף ללא תאונות";
    case CarInsuranceSubCategory.COMPREHENSIVE_NO_THEFT: return "מקיף ללא גניבה";
    default: return value;
  }
};

/**
 * Format HomeInsuranceSubCategory enum to Hebrew text
 */
export const formatHomeInsuranceSubCategory = (value: HomeInsuranceSubCategory): string => {
  switch (value) {
    case HomeInsuranceSubCategory.BUILDING: return "מבנה";
    case HomeInsuranceSubCategory.CONTENTS: return "תכולה";
    case HomeInsuranceSubCategory.COMBINED: return "משולב";
    case HomeInsuranceSubCategory.JEWELRY: return "תכשיטים";
    default: return value;
  }
};

/**
 * Format BusinessInsuranceSubCategory enum to Hebrew text
 */
export const formatBusinessInsuranceSubCategory = (value: BusinessInsuranceSubCategory): string => {
  switch (value) {
    case BusinessInsuranceSubCategory.COMPREHENSIVE_BUSINESS: return "עסק מקיף";
    default: return value;
  }
};

/**
 * Format DisabilityInsuranceSubCategory enum to Hebrew text
 */
export const formatDisabilityInsuranceSubCategory = (value: DisabilityInsuranceSubCategory): string => {
  switch (value) {
    case DisabilityInsuranceSubCategory.DISABILITY_COMPENSATION: return "אבדן כושר עבודה שחרור ופיצוי";
    case DisabilityInsuranceSubCategory.DISABILITY_WAIVER: return "אבדן כושר עבודה שחרור";
    default: return value;
  }
};

/**
 * Format ServiceContractSubCategory enum to Hebrew text
 */
export const formatServiceContractSubCategory = (value: ServiceContractSubCategory): string => {
  switch (value) {
    case ServiceContractSubCategory.ALT_MEDICINE: return "רפואה משלימה";
    case ServiceContractSubCategory.RAPID_DIAGNOSIS: return "אבחון מהיר";
    case ServiceContractSubCategory.HOSPITAL_ESCORT: return "ליווי באשפוז";
    case ServiceContractSubCategory.PERSONAL_MEDICAL_MANAGEMENT: return "ניהול רפואי אישי";
    case ServiceContractSubCategory.ONLINE_DOCTOR: return "רופא און ליין";
    case ServiceContractSubCategory.OTHER: return "אחר";
    default: return value;
  }
};

/**
 * Format LifeInsuranceSubCategory enum to Hebrew text
 */
export const formatLifeInsuranceSubCategory = (value: LifeInsuranceSubCategory): string => {
  switch (value) {
    case LifeInsuranceSubCategory.PURE_SAVINGS: return "חיסכון טהור";
    case LifeInsuranceSubCategory.MIXED_SAVINGS: return "ביטוח חיים משולב חיסכון";
    case LifeInsuranceSubCategory.DEATH_ONLY: return "מוות בלבד";
    case LifeInsuranceSubCategory.ACCIDENTAL_DEATH: return "מוות מתאונה";
    case LifeInsuranceSubCategory.DISABILITY: return "נכות";
    default: return value;
  }
};

/**
 * Format LossOfWorkCapacitySubCategory enum to Hebrew text
 */
export const formatLossOfWorkCapacitySubCategory = (value: LossOfWorkCapacitySubCategory): string => {
  switch (value) {
    case LossOfWorkCapacitySubCategory.DISABILITY_COMPENSATION: return "אבדן כושר עבודה שחרור ופיצוי";
    case LossOfWorkCapacitySubCategory.DISABILITY_WAIVER: return "אבדן כושר עבודה שחרור";
    default: return value;
  }
};

/**
 * Maps a HealthSubCategory enum value (stored in DB) to its Hebrew display text.
 *
 * Called by formatCoverageItemLabel() in healthCoverageAnalysisUtil.ts, which tries
 * formatInsuranceProductType first, then this function, then falls back to the raw string.
 *
 * Two kinds of enum values exist:
 * - Normalized (e.g. CONSULTATION, TRANSPLANTS): multiple source texts that mean the same thing
 *   all map to one enum in parse_health_subcategory, so they share one display label here.
 * - Preserved variants (e.g. CONSULTATION_AND_TESTS, TRANSPLANT_ACHIDA): source texts that we
 *   want to display exactly as they appeared in the file. Each gets its own enum value so the
 *   original wording is not lost after DB storage.
 *
 * The default branch returns the raw enum string — the last-resort fallback for old DB records
 * stored before the matching enum existed.
 */
export const formatHealthSubCategory = (value: HealthSubCategory): string => {
  switch (value) {
    // ── Surgeries ──────────────────────────────────────────────────────────────
    case HealthSubCategory.SURGERY_STANDARD:           return "ניתוחים אחידה";
    case HealthSubCategory.SUPPLEMENTARY_SURGERY_SHABAN: return 'ניתוחים משלים שב"ן';
    case HealthSubCategory.SURGERY_SHABAN_WITH_COPAY:  return 'ניתוחים אחידה משלים שב"ן עם השתתפות עצמית';
    case HealthSubCategory.SURGERY_WITH_COPAY:         return 'ניתוחים עם השתתפות עצמית';
    case HealthSubCategory.SURGERY_ACHIDA_SHABAN:      return 'ניתוחים אחידה - משלים שב"ן';
    case HealthSubCategory.SURGERY_INDEMNITY:          return 'ניתוחים עם פיצוי על פנייה לציבורי/שב"ן';
    case HealthSubCategory.SURGERY_ABROAD:             return 'ניתוחים בחו"ל';
    case HealthSubCategory.SURGERY_ABROAD_ACHIDA:      return 'ניתוחים בחו"ל-אחידה';
    case HealthSubCategory.REPLACEMENT_TREATMENTS:     return "נספח טיפולים מחליפי ניתוח";
    // ── Transplants ────────────────────────────────────────────────────────────
    case HealthSubCategory.TRANSPLANTS:                return "השתלות";
    case HealthSubCategory.TRANSPLANT_ACHIDA:          return "השתלות-אחידה";
    // ── Medications ────────────────────────────────────────────────────────────
    case HealthSubCategory.BASIC_MEDICINES:            return "תרופות בסיס";
    case HealthSubCategory.BASIC_MEDICINES_DASH:       return "תרופות - בסיס";
    case HealthSubCategory.EXTENDED_MEDICINES:         return "תרופות הרחבה";
    case HealthSubCategory.EXTENDED_MEDICINES_DASH:    return "תרופות - הרחבה";
    case HealthSubCategory.BASIC_EXTENDED_MEDICINES:   return "תרופות בסיס + הרחבה";
    case HealthSubCategory.MEDICINES_OUTSIDE_BASKET:   return "תרופות מחוץ לסל";
    case HealthSubCategory.MEDICINE_OUT_OF_BASKET_ACHIDA: return "תרופות מחוץ לסל-אחידה";
    // ── Consultation & ambulatory ──────────────────────────────────────────────
    case HealthSubCategory.CONSULTATION:               return "ייעוץ";
    case HealthSubCategory.CONSULTATION_AND_TESTS:     return "ייעוץ ובדיקות";
    case HealthSubCategory.RAPID_DIAGNOSIS:            return "אבחון מהיר";
    case HealthSubCategory.ADVANCED_TREATMENT:         return "טיפול מתקדם";
    case HealthSubCategory.ADVANCED_TREATMENT_AND_EQUIPMENT: return "טיפולים וטכנולוגיות מתקדמות ואביזרים רפואיים";
    // ── Care management ────────────────────────────────────────────────────────
    case HealthSubCategory.HOSPITAL_ESCORT:            return "ליווי באשפוז";
    case HealthSubCategory.ILLNESS_ESCORT:             return "ליווי במחלה";
    case HealthSubCategory.MEDICAL_EVENT_ESCORT:       return "ליווי וטיפולים אגב אירוע רפואי";
    case HealthSubCategory.MEDICAL_EVENT_ESCORT_TEXT:  return "ליווי וטיפולים אגב אירוע רפואי";
    case HealthSubCategory.PERSONAL_MEDICAL_MANAGEMENT: return "ניהול רפואי אישי";
    // ── Digital & other ───────────────────────────────────────────────────────
    case HealthSubCategory.ONLINE_DOCTOR:              return "רופא אונליין";
    case HealthSubCategory.ONLINE_DOCTOR_ABROAD:       return 'רופא אונליין בחו"ל';
    case HealthSubCategory.SPORTS_MEDICINE:            return "רפואת ספורט";
    case HealthSubCategory.ALT_MEDICINE:               return "רפואה משלימה";
    case HealthSubCategory.CRITICAL_ILLNESS:           return "מחלות קשות";
    case HealthSubCategory.PERSONAL_ACCIDENTS:         return "תאונות אישיות";
    case HealthSubCategory.RELOCATION:                 return "רילוקיישן";
    case HealthSubCategory.COVERAGE_PACKAGE:           return "חבילת כיסויים";
    case HealthSubCategory.OTHER:                      return "אחר";
    case HealthSubCategory.DENTAL:                     return "שיניים";
    case HealthSubCategory.DENTAL_CARE:                return "טיפולי שיניים";
    case HealthSubCategory.DENTAL_TREATMENT:           return "טיפולי שיניים";
    case HealthSubCategory.TEETH:                      return "שיניים";
    case HealthSubCategory.TOOTH:                      return "שיניים";
    default: return value;
  }
};

/**
 * Get the SubCategory enum type based on the main branch
 */
export const getSubCategoryEnumByMainBranch = (branch: MainInsuranceBranch): any => {
  switch (branch) {
    case MainInsuranceBranch.CAR:
      return CarInsuranceSubCategory;
    case MainInsuranceBranch.HOME:
      return HomeInsuranceSubCategory;
    case MainInsuranceBranch.BUSINESS:
      return BusinessInsuranceSubCategory;
    case MainInsuranceBranch.DISABILITY_LIFE:
      return DisabilityInsuranceSubCategory;
    case MainInsuranceBranch.LIFE:
      return LifeInsuranceSubCategory;
    case MainInsuranceBranch.HEALTH_ACCIDENT:
    case MainInsuranceBranch.HEALTH:
      return HealthSubCategory;
    case MainInsuranceBranch.LONG_TERM_CARE:
      return LongTermCareSubCategory;
    case MainInsuranceBranch.SERVICE_CONTRACT:
      return ServiceContractSubCategory;
    case MainInsuranceBranch.LOSS_OF_WORK_CAPACITY:
      return LossOfWorkCapacitySubCategory;
    default:
      return undefined;
  }
};

/**
 * Mapping of branch names to their sub-category formatters
 */
export const subBranchFormatters: Record<string, (value: any) => string> = {
  HEALTH: (value: string) => formatHealthSubCategory(value as any),
  HEALTH_ACCIDENT: (value: string) => formatHealthSubCategory(value as any),
  CAR: (value: string) => formatCarInsuranceSubCategory(value as any),
  HOME: (value: string) => formatHomeInsuranceSubCategory(value as any),
  BUSINESS: (value: string) => formatBusinessInsuranceSubCategory(value as any),
  DISABILITY_LIFE: (value: string) => formatDisabilityInsuranceSubCategory(value as any),
  LIFE: (value: string) => formatLifeInsuranceSubCategory(value as any),
  LOSS_OF_WORK_CAPACITY: (value: string) => formatLossOfWorkCapacitySubCategory(value as any),
  SERVICE_CONTRACT: (value: string) => formatServiceContractSubCategory(value as any),
  LONG_TERM_CARE: (value: string) => formatLongTermCareSubCategory(value as any),
};

/**
 * Get color for MainInsuranceBranch
 */
export const colorMainInsuranceBranch = (value: MainInsuranceBranch): string => {
  switch (value) {
    case MainInsuranceBranch.CAR: return "#1E88E5"; // כחול
    case MainInsuranceBranch.HOME: return "#43A047"; // ירוק
    case MainInsuranceBranch.BUSINESS: return "#F4511E"; // כתום
    case MainInsuranceBranch.DISABILITY_LIFE: return "#8E24AA"; // סגול
    case MainInsuranceBranch.LIFE: return "#5E35B1"; // סגול כהה
    case MainInsuranceBranch.SAVINGS: return "#FFB300"; // צהוב-זהב
    case MainInsuranceBranch.HEALTH_ACCIDENT: return "#FB8C00"; // כתום בהיר
    case MainInsuranceBranch.HEALTH: return "#E53935"; // אדום
    case MainInsuranceBranch.LONG_TERM_CARE: return "#00897B"; // טורקיז
    case MainInsuranceBranch.SERVICE_CONTRACT: return "#6D4C41"; // חום
    case MainInsuranceBranch.LOSS_OF_WORK_CAPACITY: return "#3949AB"; // כחול עמוק
    default: return "#9E9E9E"; // אפור ברירת מחדל
  }
};

export const formatHealthCoverageNormalizedCategory = (value: HealthCoverageNormalizedCategory): string => {
  switch (value) {
    case HealthCoverageNormalizedCategory.SURGERIES: return "ניתוחים";
    case HealthCoverageNormalizedCategory.TRANSPLANTS: return "השתלות";
    case HealthCoverageNormalizedCategory.MEDICATIONS: return "תרופות";
    case HealthCoverageNormalizedCategory.AMBULATORY: return "אמבולטורי";
    case HealthCoverageNormalizedCategory.CONSULTATION_DIAGNOSIS: return "ייעוץ ואבחון";
    case HealthCoverageNormalizedCategory.DIGITAL_SERVICES: return "רופא אונליין";
    case HealthCoverageNormalizedCategory.ALT_MEDICINE: return "רפואה משלימה";
    case HealthCoverageNormalizedCategory.CARE_MANAGEMENT: return "ליווי רפואי";
    case HealthCoverageNormalizedCategory.SPECIAL_SERVICES: return "שירותים מיוחדים";
    case HealthCoverageNormalizedCategory.CRITICAL_ILLNESS: return "מחלות קשות";
    case HealthCoverageNormalizedCategory.PERSONAL_ACCIDENTS: return "תאונות אישיות";
    case HealthCoverageNormalizedCategory.DENTAL: return "טיפולי שיניים";
    case HealthCoverageNormalizedCategory.PACKAGE_UNKNOWN: return "חבילה לא מזוהה";
    case HealthCoverageNormalizedCategory.NOISE: return "-";
    case HealthCoverageNormalizedCategory.UNCLASSIFIED: return "לא מסווג";
    default: return value;
  }
};
